var _ = require('optimizely-client-fn');
var actionTypes = require('core/constants/action_types');
var di = require('core/di');
var enums = require('core/constants/enums');
var Flux = require('core/flux');
var generateUniqueId = require('optimizely-uuid').generate;
var WindowEnv = require('core/env/window');

var DEFAULT_INTERVAL:number = require('./constants').DEFAULT_INTERVAL;

var RumStore = di.get('stores/rum');

var _intervals = {} as any;

/**
 * Wrapper for setInterval. Functions differently from setInterval in that
 * we dedup setInterval calls for a given delay by aggregating all callbacks into
 * a single method. This reduces the number of intervals running at any given time,
 * however it also has the downside of executing the initial function invocation sooner
 * than the supplied delay.
 *
 * The other difference is that this will return a cancel function, rather than an id
 * to be passed to clearInterval()
 *
 * @param {function} callback
 * @param {number} delay Interval length in ms
 * @returns {function} Function that may be invoked to cancel interval
 */
exports.poll = function(callback:Function, delay:number): Function {
  if (!_.isNumber(delay)) {
    delay = DEFAULT_INTERVAL;
  }
  if (!_intervals[delay]) {
    _intervals[delay] = {
      callbacks: {},
      id: WindowEnv.setInterval(_.partial(invokeHandlers, delay), delay),
    };
  }
  var callbackId = generateUniqueId();
  _intervals[delay].callbacks[callbackId] = callback;
  return _.partial(cancelPolling, callbackId, delay);
};

/**
 * Clears all intervals and dereferences callbacks. To be used for testing purposes.
 */
exports.cancelAll = function() {
  _.each(_intervals, function(interval, delay) {
    clearInterval(interval.id);
    delete _intervals[delay];
  });
};

/**
 * Execute all callbacks for a given interval
 * @private
 * @param {number} Interval we want to invoke callbacks for (indexed by delay)
 */
function invokeHandlers(delay) {
  if (_intervals[delay]) {
    _.each(_intervals[delay].callbacks, function(callback) {
      callback.call(null);
    });
  }
  if (__RUM_DOM_OBSERVATION_COUNTERS__ && RumStore.getSampleRum()) {
    // Record that we reacted to a setInterval handler as a means of polling.
    Flux.dispatch(actionTypes.RECORD_DOM_OBSERVATION_OCCURENCE, {
      counterName: enums.PerformanceCounters.polling_invocation
    });
  }
}

/**
 * Remove a specific callback. If all callbacks have been removed for an interval, clear the
 * interval as well.
 * @private
 * @param {string} callbackId
 * @param {number} delay
 */
function cancelPolling(callbackId:string, delay:number) {
  if (_intervals[delay]) {
    if (_intervals[delay].callbacks[callbackId]) {
      delete _intervals[delay].callbacks[callbackId];
      if (!_.some(_intervals[delay].callbacks)) {
        if (__INVARIANT__) {
          if (_.isUndefined(_intervals[delay].id)) {
            throw new Error('No ID found for interval: ' + delay);
          }
        }
        clearInterval(_intervals[delay].id);
        delete _intervals[delay];
      }
    }
  }
}
